import { useContext, useMemo, useRef } from 'react';
import { useBeforeMount } from '@24hr/react-hooks';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { useHumany } from '@/hooks/use-humany';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { humanyFaqQueryCached } from '@/queries/humany-cached';
import { removeTrailingSlash } from '@/utils/strings';
import { useLocalization } from '@/hooks/use-localization';
import { HUMANY_FAQ_QUERY_KEY } from '@/queries/humany';
import { AppContext } from '@/contexts/app-context';
import { SearchFaqsResults } from '../../layout/search/components/search-faqs-results';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SEARCH_FAQS_QUERY_KEY, searchFaqsQuery } from '@/queries/search';

export function OneWidgetFaqCached({ widgetSlug = 'kundservice', widgetType = 'private' }) {
    const navigate = useNavigate();
    const { getSetting } = useGlobalQueries();
    const { getLanguage, localize } = useLocalization();
    const { path, baseUrl, humanyData } = useHumany({ widgetType, widgetSlug });

    const faqHome = useMemo(() => getSetting('humany_settings')?.humany_page_link || '/kundservice', [getSetting]);

    const context = useContext(AppContext);
    const humanyContentRef = useRef<HTMLDivElement>(null);

    useBeforeMount(() => {
        context.prefetch({
            queryKey: [HUMANY_FAQ_QUERY_KEY, widgetSlug, path || '/'],
            queryFn: () => humanyFaqQueryCached(widgetSlug, path, baseUrl, context),
        });
    });

    const searchFaqsData = useQuery({
        queryKey: [SEARCH_FAQS_QUERY_KEY, context.globalSearchTerm],
        queryFn: () => searchFaqsQuery(context.globalSearchTerm, context),
        placeholderData: keepPreviousData,
        enabled: true,
    });

    const handleClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest('a') as HTMLAnchorElement | null;

        if (link && link.getAttribute('href')) {
            const href = link.getAttribute('href')!;

            // internal links only
            if (!/^https?:\/\//i.test(href)) {
                e.preventDefault();
                navigate(href);
            }
        }
    };

    const startPageLink = useMemo(() => {
        const startPage = getSetting('humany_settings')?.humany_page_link || '/kundservice';
        return removeTrailingSlash(startPage);
    }, [getSetting]);

    const title = useMemo(() => {
        const t = humanyData?.data?.title;
        return t ? t.charAt(0).toUpperCase() + t.slice(1) : null;
    }, [humanyData?.data?.title]);

    return (
        <div data-component="one-widget-faq" className="cached-faq">
            <div className="r-relative">
                <img
                    alt=""
                    width={20}
                    height={20}
                    src="/assets/icons/search.svg"
                    className="r-absolute r-left-4 r-top-[18px] r-max-w-[20px] r-opacity-50"
                />
                <input
                    placeholder={localize('search')}
                    value={context.globalSearchTerm}
                    onChange={({ target }) => context.setGlobalSearchTerm(target.value)}
                    className="r-br-2 r-mb-4 r-w-full r-rounded-md r-border r-border-gray-400 r-py-4 r-pl-12 r-pr-4"
                />
                {context.globalSearchTerm ? (
                    <button className="r-absolute r-right-4 r-top-[12px]" onClick={() => context.setGlobalSearchTerm('')} role="button">
                        <img src="/assets/icons/close-reverse.svg" alt={localize('close')} />
                    </button>
                ) : null}
            </div>

            {context.globalSearchTerm ? (
                <SearchFaqsResults
                    data={searchFaqsData}
                    close={() => context.setGlobalSearchTerm('')}
                    onLinkClick={(link: string) => {
                        context.setGlobalSearchTerm('');
                        navigate(link || faqHome);
                    }}
                />
            ) : (
                <>
                    {humanyData.data?.title && !humanyData.data?.title?.startsWith('browse') ? (
                        <Helmet>
                            <title>{humanyData.data.title}</title>
                        </Helmet>
                    ) : null}

                    {humanyData.data?.body && (
                        <div onClick={handleClick} ref={humanyContentRef} className="humany-one-widget-container styled-link">
                            {path && humanyData.data?.title && !humanyData.data?.title?.startsWith('browse') && (
                                <div className="styled-link r-flex r-gap-2 r-pb-2 md:r-pb-4">
                                    <Link to={startPageLink}>{getLanguage() === 'fi' ? 'Aloitus' : 'Start'}</Link>
                                    <span className="r-py-1.5 max-md:r-hidden">/</span>
                                    <span className="r-py-1.5 max-md:r-hidden">{title}</span>
                                </div>
                            )}
                            <div
                                className="cached-faq__content styled-link"
                                dangerouslySetInnerHTML={{
                                    __html: JSON.parse(humanyData.data.body),
                                    // ?.replaceAll(/visa fler/gi, '')
                                    // ?.replaceAll(/tillbaka/gi, '')
                                    // ?.replaceAll(/Hämta fler/gi, ''),
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
